import React from 'react';
import { withAlpha } from '../palette';
import { MiniGameId } from './types';

const LINE = 'rgba(255,255,255,.42)';
const FAINT = 'rgba(255,255,255,.22)';
const FILL = 'rgba(255,255,255,.05)';
const STILL_STROKE = 1.7;

interface IGlyphProps {
  accent: string;
  size?: number;
  still?: boolean;
}

const strokeOf = (width: number, still: boolean) =>
  still ? width * STILL_STROKE : width;

export const LockGlyph = ({
  accent,
  size = 40,
  still = false,
}: IGlyphProps) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden>
    <circle
      cx="20"
      cy="20"
      r="15"
      stroke={LINE}
      strokeWidth={strokeOf(1.5, still)}
    />
    {still ? null : (
      <circle
        cx="20"
        cy="20"
        r="11"
        stroke={FAINT}
        strokeWidth="2"
        strokeDasharray="1 2.45"
      />
    )}
    <path
      d="M29.64 8.51A15 15 0 0 1 34.49 16.12"
      stroke={accent}
      strokeWidth={strokeOf(3.2, still)}
      strokeLinecap="round"
    />
    <circle cx="20" cy="20" r={still ? 3.4 : 2.4} fill="rgba(255,255,255,.7)" />
    {still ? null : (
      <g className="myta-mg-spark">
        <circle cx="20" cy="5" r="2.1" fill="#fff" />
      </g>
    )}
  </svg>
);

export const StackGlyph = ({
  accent,
  size = 40,
  still = false,
}: IGlyphProps) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden>
    <rect
      x="7"
      y="28"
      width="26"
      height="6"
      rx="1.5"
      stroke={LINE}
      strokeWidth={strokeOf(1.5, still)}
      fill={FILL}
    />
    <rect
      x="10.5"
      y="20"
      width="22.5"
      height="6"
      rx="1.5"
      stroke={LINE}
      strokeWidth={strokeOf(1.5, still)}
      fill={FILL}
    />
    <rect
      x="14"
      y="12"
      width="19"
      height="6"
      rx="1.5"
      stroke={accent}
      strokeWidth={strokeOf(1.5, still)}
      fill={withAlpha(accent, still ? 0.5 : 0.42)}
    />
    {still ? null : (
      <>
        <path
          d="M15.5 12.9H31.5"
          stroke="#fff"
          strokeOpacity=".75"
          strokeWidth="1"
          strokeLinecap="round"
        />
        <g className="myta-mg-chunk">
          <rect
            x="7.5"
            y="12"
            width="5"
            height="6"
            rx="1.3"
            stroke={accent}
            strokeWidth="1.3"
            fill={withAlpha(accent, 0.3)}
          />
        </g>
      </>
    )}
  </svg>
);

export const SliceGlyph = ({
  accent,
  size = 40,
  still = false,
}: IGlyphProps) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden>
    <path
      d="M7.9 13L20 6L32.1 13V27Z"
      transform="translate(1 -1.75)"
      stroke={LINE}
      strokeWidth={strokeOf(1.5, still)}
      strokeLinejoin="round"
      fill={FILL}
    />
    <path
      d="M7.9 13L32.1 27L20 34L7.9 27Z"
      transform="translate(-1 1.75)"
      stroke={LINE}
      strokeWidth={strokeOf(1.5, still)}
      strokeLinejoin="round"
      fill={FILL}
    />
    <path
      d="M4.4 11L35.6 29"
      stroke={accent}
      strokeWidth={strokeOf(1.7, still)}
      strokeLinecap="round"
    />
    {still ? null : (
      <g className="myta-mg-blade">
        <circle cx="4.4" cy="11" r="1.9" fill="#fff" />
      </g>
    )}
  </svg>
);

const WASD_KEYS = [
  { label: 'W', x: 12, y: 0, lit: true },
  { label: 'A', x: 0, y: 12, lit: false },
  { label: 'S', x: 12, y: 12, lit: false },
  { label: 'D', x: 24, y: 12, lit: false },
];

export const WasdGlyph = ({ accent, size = 34 }: IGlyphProps) => (
  <svg
    width={size}
    height={(size * 22.5) / 34.5}
    viewBox="0 0 34.5 22.5"
    fill="none"
    aria-hidden
  >
    {WASD_KEYS.map((key) => (
      <g key={key.label}>
        <rect
          x={key.x + 0.5}
          y={key.y + 0.5}
          width="9.5"
          height="9.5"
          rx="2.4"
          stroke={key.lit ? accent : 'rgba(255,255,255,.55)'}
          strokeWidth="1"
          fill={key.lit ? withAlpha(accent, 0.36) : 'rgba(255,255,255,.1)'}
        />
        <text
          x={key.x + 5.25}
          y={key.y + 7.6}
          textAnchor="middle"
          fontFamily="'JetBrains Mono', monospace"
          fontSize="6.6"
          fontWeight="700"
          fill="#fff"
        >
          {key.label}
        </text>
      </g>
    ))}
  </svg>
);

const TutorialGlyph = ({ accent, size = 40 }: IGlyphProps) => (
  <WasdGlyph accent={accent} size={size * 0.8} />
);

const GLYPHS: Record<MiniGameId, (props: IGlyphProps) => JSX.Element> = {
  lock: LockGlyph,
  stack: StackGlyph,
  slice: SliceGlyph,
  controls: TutorialGlyph,
};

export const GameGlyph = ({
  id,
  ...props
}: IGlyphProps & { id: MiniGameId }) => {
  const Glyph = GLYPHS[id];

  return <Glyph {...props} />;
};
